import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../../utils/api';

// Cache for grill data to avoid repeated API calls
// Keyed by appliesTo value to support different product types
const grillDataCacheByType = {};
const cachePromiseByType = {};

/**
 * Hook to fetch and manage grill data from the backend
 * @param {string} appliesTo - Filter by product type: 'windows', 'patiodoors', or undefined for all
 * Data structure from API:
 * {
 *   grillTypes: [{ id, label, value, applies_to, active }],
 *   grillSizes: [{ id, fkGrillType, label, value, applies_to, active }],
 *   grillPatterns: [{ id, fkGrillSize, label, value, applies_to, active }],
 *   grillInternalColors: [{ id, label, value, active }],
 *   grillExternalColors: [{ id, label, value, active }]
 * }
 */
export const useGrillData = (appliesTo = 'windows') => {
  const cacheKey = appliesTo || 'all';
  const [data, setData] = useState(grillDataCacheByType[cacheKey]);
  const [loading, setLoading] = useState(!grillDataCacheByType[cacheKey]);
  const [error, setError] = useState(null);

  const fetchGrillData = useCallback(async (forceRefresh = false) => {
    // Return cached data if available and not forcing refresh
    if (grillDataCacheByType[cacheKey] && !forceRefresh) {
      setData(grillDataCacheByType[cacheKey]);
      setLoading(false);
      return grillDataCacheByType[cacheKey];
    }

    // If already fetching, wait for that promise
    if (cachePromiseByType[cacheKey] && !forceRefresh) {
      try {
        const result = await cachePromiseByType[cacheKey];
        setData(result);
        setLoading(false);
        return result;
      } catch (e) {
        setError(e.message);
        setLoading(false);
        return null;
      }
    }

    setLoading(true);
    setError(null);

    // Build URL with optional applies_to filter
    const url = appliesTo ? `/grills/?applies_to=${appliesTo}` : '/grills/';

    cachePromiseByType[cacheKey] = authFetch(url)
      .then((result) => {
        grillDataCacheByType[cacheKey] = result;
        setData(result);
        setLoading(false);
        return result;
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
        cachePromiseByType[cacheKey] = null;
        throw e;
      });

    return cachePromiseByType[cacheKey];
  }, [appliesTo, cacheKey]);

  useEffect(() => {
    if (!grillDataCacheByType[cacheKey]) {
      fetchGrillData();
    }
  }, [fetchGrillData, cacheKey]);

  // Get active grill types
  const getGrillTypes = useCallback(() => {
    if (!data?.grillTypes) return [];
    return data.grillTypes.filter(t => t.active);
  }, [data]);

  // Get active grill sizes filtered by grill type
  // Only show sizes that match the specific grillTypeId
  const getGrillSizes = useCallback((grillTypeId) => {
    if (!data?.grillSizes) return [];
    if (grillTypeId === undefined) {
      // No filter - return all active sizes
      return data.grillSizes.filter(s => s.active);
    }
    // Return only sizes matching this specific grill type
    return data.grillSizes.filter(s => s.active && s.fkGrillType === grillTypeId);
  }, [data]);

  // Get active grill patterns filtered by grill size
  // Only show patterns that match the specific grillSizeId
  const getGrillPatterns = useCallback((grillSizeId) => {
    if (!data?.grillPatterns) return [];
    if (grillSizeId === undefined) {
      // No filter - return all active patterns
      return data.grillPatterns.filter(p => p.active);
    }
    // Return only patterns matching this specific grill size
    return data.grillPatterns.filter(p => p.active && p.fkGrillSize === grillSizeId);
  }, [data]);

  // Get active grill internal colors
  const getGrillInternalColors = useCallback(() => {
    if (!data?.grillInternalColors) return [];
    return data.grillInternalColors.filter(c => c.active);
  }, [data]);

  // Get active grill external colors
  const getGrillExternalColors = useCallback(() => {
    if (!data?.grillExternalColors) return [];
    return data.grillExternalColors.filter(c => c.active);
  }, [data]);

  // Find grill type ID by value
  const getGrillTypeId = useCallback((value) => {
    if (!data?.grillTypes) return null;
    const found = data.grillTypes.find(t => t.value === value);
    return found?.id || null;
  }, [data]);

  // Find grill size ID by value (optionally filtered by grill type ID)
  const getGrillSizeId = useCallback((value, grillTypeId) => {
    if (!data?.grillSizes) return null;
    let found;
    if (grillTypeId !== undefined) {
      // Find size matching both value and grill type
      found = data.grillSizes.find(s => s.value === value && s.fkGrillType === grillTypeId);
    } else {
      // Fall back to just matching value
      found = data.grillSizes.find(s => s.value === value);
    }
    return found?.id || null;
  }, [data]);

  // Clear cache (useful for admin updates)
  const clearCache = useCallback(() => {
    // Clear all caches
    Object.keys(grillDataCacheByType).forEach(key => {
      delete grillDataCacheByType[key];
    });
    Object.keys(cachePromiseByType).forEach(key => {
      delete cachePromiseByType[key];
    });
  }, []);

  return {
    data,
    loading,
    error,
    refresh: () => fetchGrillData(true),
    clearCache,
    getGrillTypes,
    getGrillSizes,
    getGrillPatterns,
    getGrillInternalColors,
    getGrillExternalColors,
    getGrillTypeId,
    getGrillSizeId
  };
};

export default useGrillData;
